/**
 * Remove unfilled OPTIONAL fields (value === null) from an e-form payload so a
 * sparse payload validates against a schema whose optionals are typed as
 * non-nullable (e.g. SGTraDex DEs, whose optionals are bare {"type":"string"} /
 * {"type":"number"} rather than ["string","null"]).
 *
 * The e-form's getFullJson (hooks/use-eform) materializes the full schema tree
 * and sets every unfilled optional leaf to null. AJV then rejects those nulls
 * against the non-nullable types, so the Submit button never enables and the
 * downstream Go producer would reject the send. Stripping the optional nulls
 * produces the sparse, non-null shape that both AJV and the producer expect.
 *
 * Schema-aware: a key is dropped only when its value is null AND it is NOT in
 * its parent's `required` list — a required-but-empty field keeps its null so
 * validation still fails and the user is prompted to fill it.
 *
 * Immutable: returns a new value and never mutates `payload`.
 *
 * @param {*} payload - the e-form payload (or a nested fragment of it).
 * @param {Record<string, any>} [schema] - the schema node describing `payload`.
 * @returns {*} a new payload with unfilled optional nulls removed.
 */
export default function stripEmptyOptionals(payload, schema) {
	if (payload === null || typeof payload !== "object") {
		return payload;
	}

	if (Array.isArray(payload)) {
		const itemSchema = schema?.items;
		return payload.map((item) => stripEmptyOptionals(item, itemSchema));
	}

	const required = Array.isArray(schema?.required) ? schema.required : [];
	const properties = schema?.properties ?? {};
	const result = {};

	for (const key of Object.keys(payload)) {
		const value = payload[key];

		// Drop unfilled optional leaves; keep required nulls so a required-empty
		// field still fails validation.
		if (value === null && !required.includes(key)) {
			continue;
		}

		result[key] =
			value !== null && typeof value === "object"
				? stripEmptyOptionals(value, properties[key])
				: value;
	}

	return result;
}
